import { useEffect, useState, type ReactNode } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { getAuthServiceBaseUrl } from "../config";
import { getTenantSubdomain, isGenericPortal } from "../utils/subdomain";
import {
  authCorrelationHeaders,
  createRequestId,
  recordAuthPageAction,
  startTimer,
} from "../utils/perfTelemetry";

type GateState = "loading" | "ok" | "invalid";

/**
 * Validates tenant subdomain before rendering the app (nginx also gates via validate-subdomain).
 */
export default function TenantGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>("loading");

  useEffect(() => {
    const slug = getTenantSubdomain();
    if (!slug || isGenericPortal()) {
      setState("ok");
      return;
    }

    const base = getAuthServiceBaseUrl();
    if (!base) {
      setState("ok");
      return;
    }

    let cancelled = false;
    const requestId = createRequestId();
    const elapsed = startTimer();
    fetch(`${base}/auth/validate-subdomain?slug=${encodeURIComponent(slug)}`, {
      method: "GET",
      headers: authCorrelationHeaders(requestId),
    })
      .then((res) => {
        if (cancelled) return;
        const ok = res.ok;
        recordAuthPageAction({
          action: "validate_subdomain",
          outcome: ok ? "success" : "not_found",
          duration_ms: elapsed(),
          requestId,
          status: res.status,
        });
        setState(ok ? "ok" : "invalid");
      })
      .catch(() => {
        if (!cancelled) {
          recordAuthPageAction({
            action: "validate_subdomain",
            outcome: "failure",
            duration_ms: elapsed(),
            requestId,
            failure_stage: "network",
            status: 0,
            // fail open to match server behavior
          });
          setState("ok");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (state === "invalid") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 1,
          px: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Company not found
        </Typography>
        <Typography color="text.secondary">
          This company URL does not exist or is no longer active. Check the link you received or contact your administrator.
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
}
